import { useState, useCallback, useRef, useEffect } from 'react'
import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { AlertCircle, Loader2 } from 'lucide-react'
import {
  Table,
  TableHeader,
  TableBody,
  TableFooter,
  TableHead,
  TableRow,
  TableCell
} from '@/components/ui/table'
import { supabase } from '@/supabase-client'
import { toast } from 'sonner'

const FUNCTION_NAME = 'cost-tracking'

function formatCurrency(value) {
  if (!value && value !== 0) return '—'
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value)
}

function formatMonthLabel(monthStr) {
  const d = new Date(monthStr)
  return d.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })
}

function capitalise(str) {
  return str.charAt(0).toUpperCase() + str.slice(1)
}

function parseCurrencyInput(value) {
  if (!value) return 0
  const cleaned = value.replace(/[£,\s]/g, '')
  const num = parseFloat(cleaned)
  return isNaN(num) ? 0 : num
}

async function saveCost(periodStart, category, amount) {
  const [year, month] = periodStart.split('-')
  const normalised = `${year}-${month}-01`

  const { data, error } = await supabase.functions.invoke(
    `${FUNCTION_NAME}/fixed-costs`,
    {
      method: 'POST',
      body: {
        period_start: normalised,
        category,
        amount,
        description: null
      }
    }
  )

  if (error) throw new Error(error.message || 'Save failed')
  if (data?.error) throw new Error(data.message || data.error)
  return data
}

/**
 * Inline editable cell for fixed cost amounts
 */
function EditableCell({ value, isXero, isComputed, month, category, onSaved }) {
  const isReadOnly = isXero || isComputed
  const [editing, setEditing] = useState(false)
  const [localValue, setLocalValue] = useState('')
  const [saving, setSaving] = useState(false)
  const inputRef = useRef(null)

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [editing])

  const handleClick = () => {
    if (isReadOnly || saving) return
    setLocalValue(value ? value.toFixed(2) : '')
    setEditing(true)
  }

  const handleBlur = async () => {
    const numeric = parseCurrencyInput(localValue)

    // No change — just close
    if (numeric === (value || 0)) {
      setEditing(false)
      return
    }

    setSaving(true)
    try {
      await saveCost(month, category, numeric)
      onSaved()
      toast.success('Saved')
    } catch (err) {
      toast.error('Save failed', { description: err.message })
    } finally {
      setSaving(false)
      setEditing(false)
    }
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.target.blur()
    } else if (e.key === 'Escape') {
      setEditing(false)
    }
  }

  if (editing) {
    return (
      <div className="flex items-center gap-1">
        <span className="text-muted-foreground text-xs">£</span>
        <input
          ref={inputRef}
          type="text"
          value={localValue}
          onChange={(e) => setLocalValue(e.target.value)}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          disabled={saving}
          className="w-full bg-transparent border-b border-primary text-right text-sm font-medium outline-none py-0.5"
        />
        {saving && <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />}
      </div>
    )
  }

  const isEmpty = !value || value === 0
  const displayValue = isEmpty ? '—' : formatCurrency(value)

  return (
    <div
      onClick={handleClick}
      className={`
        text-right text-sm py-0.5 rounded px-1 -mx-1
        ${isReadOnly ? 'bg-blue-50 text-foreground cursor-default' : 'cursor-pointer hover:bg-muted/60'}
        ${isEmpty && !isReadOnly ? 'text-muted-foreground' : 'font-medium'}
      `}
      title={isComputed ? 'Computed from energy meter data (read-only)' : isXero ? 'Xero-synced (read-only)' : 'Click to edit'}
    >
      {displayValue}
    </div>
  )
}

export default function FixedCostsTable({
  months,
  grid,
  categories,
  rowTotals,
  columnTotals,
  grandTotal,
  isLoading,
  error,
  onRefresh
}) {
  if (error) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>Failed to load costs: {error}</AlertDescription>
      </Alert>
    )
  }

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="space-y-3">
            {[1, 2, 3, 4, 5].map(i => (
              <Skeleton key={i} className="h-8 w-full" />
            ))}
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="sticky left-0 bg-background z-10 min-w-[100px]">Month</TableHead>
              {categories.map(cat => (
                <TableHead key={cat} className="text-right min-w-[100px]">
                  {capitalise(cat)}
                </TableHead>
              ))}
              <TableHead className="text-right min-w-[110px] font-semibold">Total</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {months.map(month => (
              <TableRow key={month}>
                <TableCell className="sticky left-0 bg-background z-10 font-medium text-sm">
                  {formatMonthLabel(month)}
                </TableCell>
                {categories.map(cat => {
                  const cell = grid[month]?.[cat]
                  return (
                    <TableCell key={cat} className="p-1.5">
                      <EditableCell
                        value={cell?.total || 0}
                        isXero={cell?.hasXero || false}
                        isComputed={cell?.isComputed || false}
                        month={month}
                        category={cat}
                        onSaved={onRefresh}
                      />
                    </TableCell>
                  )
                })}
                <TableCell className="text-right text-sm font-semibold">
                  {formatCurrency(rowTotals[month] || 0)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TableCell className="sticky left-0 z-10 font-semibold">Total</TableCell>
              {categories.map(cat => (
                <TableCell key={cat} className="text-right font-semibold text-sm">
                  {formatCurrency(columnTotals[cat] || 0)}
                </TableCell>
              ))}
              <TableCell className="text-right font-bold text-sm">
                {formatCurrency(grandTotal || 0)}
              </TableCell>
            </TableRow>
          </TableFooter>
        </Table>
      </CardContent>
    </Card>
  )
}
